import React, { useEffect, useMemo, useRef, useState } from 'react';
import { studentApi, SectionQuestionV2, AnswerV2, AnswerDetailV2, ListingStyleV2 } from '../services/api';
import VideoWithSpeed from './VideoWithSpeed';
import { displayBookSectionLabel } from '../utils/bookSectionGrouping';
import { setFunctional } from '../utils/cookieConsent';

/**
 * Unified test runner (P3) — replaces both HomeworkTestRunner and the inline
 * runner in BookContent. Renders BOTH content models:
 *
 *   • legacy (image + A–E letter pills, answers keyed by question_number) —
 *     books still served by the ple_question_map / pledb / S3 chain, and
 *     migrated books whose questions arrive as letter-text multiple choice;
 *   • new model (P3): multiple_choice with real option texts, fill_blank_mc
 *     (durum cards: öncesi + boşluk + sonrası, per-durum option sets),
 *     true_false (statement rows with Doğru/Yanlış), video_only (unscored).
 *
 * A question renders in new-model form when `question_type` is present.
 * Submission: legacy letters payload, or the v2 payload when the section
 * carries a test_id and any question has new-model fields.
 *
 * Draft persistence:
 *   • storageScope set (homework) → localStorage, key `homework-test:{scope}`
 *   • serverDraft (library)       → PUT /student/draft/:bookId/:key where key
 *     is section_key ("t{testId}") for new-model sections, else the label.
 */

export interface RunnerSection {
  section_label: string;
  section_display_label?: string | null;
  section_key?: string;
  test_id?: number | null;
  question_count: number;
  question_numbers: number[];
  max_options: number;
  points_total?: number;
  questions: SectionQuestionV2[];
  latest_attempt: { score_correct: number; score_total: number; time_spent_seconds: number | null } | null;
  answers: ReviewAnswer[] | null;
}

export interface ReviewAnswer {
  question_number: number;
  correct_option: string | null;
  selected_option: string | null;
  is_correct: boolean;
  question_id?: number | null;
  points_earned?: number | null;
  points_total?: number | null;
  detail?: AnswerDetailV2 | null;
}

interface CaseAnswer { selected_option_id?: number | null; selected_bool?: boolean | null }
interface V2QuestionAnswer { selected_option_id?: number | null; cases?: Record<number, CaseAnswer> }
type V2State = Record<number, V2QuestionAnswer>; // keyed by question_id

interface Props {
  bookId: number;
  sectionLabel: string;
  testId?: number | null;
  /** Pre-loaded section (BookContent already has it) — skips the fetch. */
  section?: RunnerSection | null;
  /** localStorage draft scope (homework mode). */
  storageScope?: string;
  /** Server-side draft persistence (library mode). */
  serverDraft?: boolean;
  submissionId?: string;
  /** Anti-cheat: solution videos were watched before the attempt — submit blank. */
  blankOnSubmit?: boolean;
  /**
   * 'question' (default) — the classic one-question-at-a-time runner with
   * images. 'optik' — a full-page answer sheet (bubble grid) that loads NO
   * question images; the student solves from the printed book and only marks
   * A–E here. Submission/scoring/draft/summary are identical to 'question'.
   */
  mode?: 'question' | 'optik';
  onClose: () => void;
  onCompleted?: (result: { score_correct: number; score_total: number }) => void;
}

const OPTION_LETTERS = 'ABCDEFGHIJ';
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

function caseLabel(i: number, style?: ListingStyleV2): string {
  if (style === 'number') return String(i + 1);
  if (style === 'roman') return ROMAN[i] ?? String(i + 1);
  return OPTION_LETTERS[i] ?? String(i + 1);
}

function formatMMSS(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function isNewModel(q: SectionQuestionV2): boolean {
  return q.question_type != null;
}

// Migrated MC questions carry bare letters as option text ("A", "B"…) — the
// content lives in the image. Render those as the classic letter pill row.
function isLetterOnlyMc(q: SectionQuestionV2): boolean {
  return q.question_type === 'multiple_choice'
    && (q.options ?? []).length > 0
    && (q.options ?? []).every((o, i) => o.text.trim().toUpperCase() === OPTION_LETTERS[i]);
}

function isAnswerable(q: SectionQuestionV2): boolean {
  return q.question_type !== 'video_only';
}

const TestRunner: React.FC<Props> = ({
  bookId, sectionLabel, testId, section: sectionProp, storageScope, serverDraft,
  submissionId, blankOnSubmit, mode = 'question', onClose, onCompleted,
}) => {
  const optik = mode === 'optik';
  const storageKey = useMemo(() => (storageScope ? `homework-test:${storageScope}` : null), [storageScope]);

  const [loading, setLoading] = useState(!sectionProp);
  const [section, setSection] = useState<RunnerSection | null>(sectionProp ?? null);
  const [error, setError] = useState<string | null>(null);

  // Legacy answers by question_number; v2 answers by question_id.
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [v2, setV2] = useState<V2State>({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());
  const [savedSeconds, setSavedSeconds] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    score_correct: number; score_total: number;
    points_correct?: number | null; points_total?: number | null;
    time: number; review: ReviewAnswer[];
  } | null>(null);
  const [phase, setPhase] = useState<'taking' | 'summary' | 'review'>('taking');
  const [reviewIdx, setReviewIdx] = useState(0);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  // True once the stored draft has been READ and APPLIED to the state above (or there
  // was none). Autosave waits for it: the state starts empty, so a save that fires
  // before the restore writes that emptiness over the draft it was about to restore.
  const [hydrated, setHydrated] = useState(false);

  const questions = section?.questions ?? [];
  const answerable = useMemo(() => questions.filter(isAnswerable), [questions]);
  const useV2Submit = !!section?.test_id && questions.some(isNewModel);
  const draftKey = section?.section_key ?? sectionLabel;

  // ── Draft persistence ──────────────────────────────────────────────────────
  //
  // ⚠️ NEVER SAVE BEFORE `hydrated`. The homework path saves 250 ms after mount, but
  // the draft is only read once the book content has been fetched — usually longer
  // than that — so the first save used to overwrite the half-done draft with the
  // empty initial state (no answers, a fresh start time, question 1) and the
  // student came back to nothing. The library path was only racy: its 1200 ms timer
  // lost to a slow `loadDraft`. Both now wait for the same gate.
  const saveTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (result || !hydrated) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      const body = { answers, v2, startedAt, currentIdx };
      if (storageKey) {
        setFunctional(storageKey, JSON.stringify(body));
      } else if (serverDraft && section) {
        const time = savedSeconds + Math.round((Date.now() - startedAt) / 1000);
        // Draft body is opaque JSON to the endpoint; v2 state rides inside
        // `answers` so the legacy loadDraft shape keeps working.
        studentApi.saveDraft(bookId, draftKey, { ...answers, __v2: v2 } as any, time).catch(() => {});
      }
    }, storageKey ? 250 : 1200);
    return () => { if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current); };
  }, [answers, v2, startedAt, currentIdx, storageKey, serverDraft, section, bookId, draftKey, savedSeconds, result, hydrated]);

  const restoreDraft = (raw: any) => {
    if (!raw || typeof raw !== 'object') return;
    const a = raw.answers && typeof raw.answers === 'object' ? { ...raw.answers } : {};
    if (a.__v2 && typeof a.__v2 === 'object') {
      setV2(a.__v2);
      delete a.__v2;
    } else if (raw.v2 && typeof raw.v2 === 'object') {
      setV2(raw.v2);
    }
    // Legacy letter answers — drop non-numeric keys defensively.
    const clean: Record<number, string> = {};
    for (const [k, v] of Object.entries(a)) {
      const n = Number(k);
      if (Number.isFinite(n) && typeof v === 'string') clean[n] = v;
    }
    setAnswers(clean);
    if (typeof raw.startedAt === 'number') setStartedAt(raw.startedAt);
    if (typeof raw.currentIdx === 'number') setCurrentIdx(raw.currentIdx);
    if (typeof raw.timeSpentSeconds === 'number') setSavedSeconds(raw.timeSpentSeconds);
  };

  // ── Load section (when not passed) + draft ────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    // A different test (or storage scope) under the same mounted runner must not
    // inherit the previous one's save permission — or write its answers into the new key.
    setHydrated(false);
    const withSection = async (sec: RunnerSection) => {
      if (cancelled) return;
      setSection(sec);
      if (storageKey) {
        try { restoreDraft(JSON.parse(localStorage.getItem(storageKey) ?? 'null')); } catch { /* ignore */ }
      } else if (serverDraft) {
        try {
          const res = await studentApi.loadDraft(bookId, sec.section_key ?? sectionLabel);
          if (!cancelled) restoreDraft(res.data);
        } catch { /* no draft */ }
      }
      // Last, so the render that lets autosave run is the one that carries the
      // restored state (the setters above and this one batch together).
      if (!cancelled) setHydrated(true);
    };
    if (sectionProp) {
      withSection(sectionProp);
      setLoading(false);
      return () => { cancelled = true; };
    }
    studentApi.bookContent(String(bookId))
      .then(async (res) => {
        if (cancelled) return;
        const secs: RunnerSection[] = res.data?.sections ?? [];
        const sec = (testId != null ? secs.find((s) => s.test_id === testId) : undefined)
          ?? secs.find((s) => s.section_label === sectionLabel);
        if (!sec) {
          setError(`"${sectionLabel}" bölümü bu kitapta bulunamadı.`);
          return;
        }
        await withSection(sec);
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error ?? e.message ?? 'Test yüklenemedi'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, sectionLabel, testId, storageKey]);

  // Live timer
  useEffect(() => {
    if (result) return;
    const tick = () => setElapsed(savedSeconds + Math.round((Date.now() - startedAt) / 1000));
    tick();
    const h = window.setInterval(tick, 500);
    return () => window.clearInterval(h);
  }, [startedAt, savedSeconds, result]);

  // ── Answer-state helpers ───────────────────────────────────────────────────
  const setMcAnswer = (q: SectionQuestionV2, optionId: number) =>
    setV2((prev) => ({ ...prev, [q.question_id!]: { ...prev[q.question_id!], selected_option_id: optionId } }));
  const setCaseAnswer = (q: SectionQuestionV2, caseId: number, val: CaseAnswer) =>
    setV2((prev) => ({
      ...prev,
      [q.question_id!]: {
        ...prev[q.question_id!],
        cases: { ...(prev[q.question_id!]?.cases ?? {}), [caseId]: val },
      },
    }));

  const isAnswered = (q: SectionQuestionV2): boolean => {
    if (!isAnswerable(q)) return false;
    if (isNewModel(q) && useV2Submit) {
      const a = v2[q.question_id!];
      if (!a) return false;
      if (q.question_type === 'multiple_choice') return a.selected_option_id != null;
      const caseIds = (q.cases ?? []).map((c) => c.id);
      return caseIds.length > 0 && caseIds.every((cid) => {
        const ca = a.cases?.[cid];
        return ca != null && (ca.selected_option_id != null || ca.selected_bool != null);
      });
    }
    return answers[q.question_number] != null;
  };
  const answeredCount = answerable.filter(isAnswered).length;

  // ── Optik (answer-sheet) helpers ───────────────────────────────────────────
  // Optik only makes sense for letter-based A–E questions (legacy image books
  // and migrated/new multiple_choice). Durum types (fill_blank_mc, true_false)
  // and video_only can't be reduced to a single A–E bubble, so the sheet marks
  // them as "solve in the test screen" instead.
  const isOptikAnswerable = (q: SectionQuestionV2): boolean =>
    isAnswerable(q) && (!isNewModel(q) || q.question_type === 'multiple_choice');
  const optikMaxOpts = (q: SectionQuestionV2): number =>
    isNewModel(q) && q.question_type === 'multiple_choice'
      ? (q.options?.length ?? section!.max_options)
      : section!.max_options;
  // Store the picked letter in the legacy `answers` map; buildAnswersV2 already
  // converts letters → option ids for the v2 submit path, so this one map is the
  // single source of truth for both content models. Clicking the same letter
  // clears it (leave blank).
  const setOptikAnswer = (q: SectionQuestionV2, letter: string) =>
    setAnswers((prev) => {
      const next = { ...prev };
      if (next[q.question_number] === letter) delete next[q.question_number];
      else next[q.question_number] = letter;
      return next;
    });
  const optikAnswerable = questions.filter(isOptikAnswerable);
  const optikAnsweredCount = optikAnswerable.filter((q) => answers[q.question_number] != null).length;

  // ── Shared A–E letter helpers ──────────────────────────────────────────────
  // Drive both the classic pill row (below the question image) and the quick-
  // answer rows in the right-hand "Sorular" panel. They read/write the SAME
  // state the main area uses — legacy letters in `answers`, new-model MC in
  // `v2` — so a pick in either place shows up in the other and `isAnswered`
  // (grid colour + counter) stays correct. Durum/true_false/video_only aren't
  // single-letter, so the panel shows them as navigate-only.
  const letterAnswerable = (q: SectionQuestionV2): boolean =>
    isAnswerable(q) && (!isNewModel(q) || q.question_type === 'multiple_choice');
  const letterCountFor = (q: SectionQuestionV2): number =>
    isNewModel(q) && q.question_type === 'multiple_choice'
      ? (q.options?.length ?? section!.max_options)
      : section!.max_options;
  const selectedLetterFor = (q: SectionQuestionV2): string | null => {
    if (isNewModel(q) && q.question_type === 'multiple_choice') {
      const selId = v2[q.question_id!]?.selected_option_id;
      const i = (q.options ?? []).findIndex((o) => o.id === selId);
      return i >= 0 ? OPTION_LETTERS[i] : (answers[q.question_number] ?? null);
    }
    return answers[q.question_number] ?? null;
  };
  const pickLetter = (q: SectionQuestionV2, i: number) => {
    if (isNewModel(q) && q.question_type === 'multiple_choice') {
      if (q.options?.[i]) setMcAnswer(q, q.options[i].id);
    } else {
      setAnswers((prev) => ({ ...prev, [q.question_number]: OPTION_LETTERS[i] }));
    }
  };

  const buildAnswersV2 = (): AnswerV2[] => {
    const rows: AnswerV2[] = [];
    for (const q of questions) {
      if (!isAnswerable(q) || q.question_id == null) continue;
      const a = v2[q.question_id];
      if (q.question_type === 'multiple_choice') {
        // Migrated letter-only questions may also carry a legacy letter answer
        // from a restored old draft — translate it to the option id.
        let selId = a?.selected_option_id ?? null;
        if (selId == null && answers[q.question_number] && q.options?.length) {
          const li = OPTION_LETTERS.indexOf(answers[q.question_number].toUpperCase());
          if (li >= 0 && li < q.options.length) selId = q.options[li].id;
        }
        rows.push({ question_id: q.question_id, selected_option_id: selId });
      } else {
        rows.push({
          question_id: q.question_id,
          cases: (q.cases ?? []).map((c) => ({ case_id: c.id, ...(a?.cases?.[c.id] ?? {}) })),
        });
      }
    }
    return rows;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const submit = async () => {
    if (!section) return;
    setConfirmSubmit(false);
    setSubmitting(true);
    setSubmitError(null);
    try {
      const timeSpent = savedSeconds + Math.round((Date.now() - startedAt) / 1000);
      const res = useV2Submit
        ? await studentApi.submitSectionAttempt(
            bookId, section.section_label, {}, timeSpent, submissionId,
            { testId: section.test_id!, answersV2: blankOnSubmit ? [] : buildAnswersV2() },
          )
        : await studentApi.submitSectionAttempt(
            bookId, section.section_label, blankOnSubmit ? {} : answers, timeSpent, submissionId,
          );
      if (storageKey) { try { localStorage.removeItem(storageKey); } catch { /* ignore */ } }
      setResult({
        score_correct: res.data.score_correct,
        score_total: res.data.score_total,
        points_correct: res.data.points_correct,
        points_total: res.data.points_total,
        time: timeSpent,
        review: (res.data.answers ?? []) as ReviewAnswer[],
      });
      setPhase('summary');
      setReviewIdx(0);
      onCompleted?.({ score_correct: res.data.score_correct, score_total: res.data.score_total });
    } catch (e: any) {
      setSubmitError(e?.response?.data?.error ?? e.message ?? 'Gönderim başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Per-type answer area (taking phase) ────────────────────────────────────
  const renderAnswerArea = (q: SectionQuestionV2) => {
    // Legacy branch questions or letter-only migrated MC → classic pill row.
    if (!isNewModel(q) || isLetterOnlyMc(q)) {
      const maxOpts = isLetterOnlyMc(q) ? (q.options?.length ?? section!.max_options) : section!.max_options;
      const selectedLetter = isNewModel(q)
        ? (() => {
            const selId = v2[q.question_id!]?.selected_option_id;
            const i = (q.options ?? []).findIndex((o) => o.id === selId);
            return i >= 0 ? OPTION_LETTERS[i] : answers[q.question_number];
          })()
        : answers[q.question_number];
      return (
        <div className="flex gap-2">
          {OPTION_LETTERS.slice(0, maxOpts).split('').map((letter, i) => {
            const selected = selectedLetter === letter;
            return (
              <label
                key={letter}
                onClick={() => {
                  if (isNewModel(q)) setMcAnswer(q, q.options![i].id);
                  else setAnswers({ ...answers, [q.question_number]: letter });
                }}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-[12px] border cursor-pointer transition-colors ${
                  selected ? 'border-[#3973bf] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center ${selected ? 'border-[#3973bf]' : 'border-gray-300'}`}>
                  {selected && <div className="w-[11px] h-[11px] rounded-full bg-[#3973bf]" />}
                </div>
                <span className="text-[15px] font-semibold text-[#2b2b2b]">{letter}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (q.question_type === 'multiple_choice') {
      const selId = v2[q.question_id!]?.selected_option_id;
      return (
        <div className="flex flex-col gap-2">
          {(q.options ?? []).map((o, i) => {
            const selected = selId === o.id;
            return (
              <label
                key={o.id}
                onClick={() => setMcAnswer(q, o.id)}
                className={`flex items-center gap-3 p-3 rounded-[12px] border cursor-pointer transition-colors ${
                  selected ? 'border-[#3973bf] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className={`w-[26px] h-[26px] shrink-0 rounded-full border-2 flex items-center justify-center text-[13px] font-bold ${selected ? 'border-[#3973bf] text-[#3973bf]' : 'border-gray-300 text-gray-500'}`}>
                  {OPTION_LETTERS[i]}
                </span>
                <span className="text-[15px] text-[#2b2b2b]">{o.text}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (q.question_type === 'fill_blank_mc') {
      return (
        <div className="flex flex-col gap-4">
          {(q.cases ?? []).map((c, ci) => {
            const ca = v2[q.question_id!]?.cases?.[c.id];
            const selectedOpt = (c.options ?? []).find((o) => o.id === ca?.selected_option_id);
            return (
              <div key={c.id} className="rounded-[14px] border border-gray-200 p-4">
                <div className="flex flex-wrap items-center gap-2 mb-3 text-[15px] text-[#2b2b2b]">
                  <span className="w-[26px] h-[26px] shrink-0 rounded-full bg-[#3973bf]/10 text-[#3973bf] flex items-center justify-center text-[13px] font-bold">
                    {caseLabel(ci, q.listing_style)}
                  </span>
                  {c.before_text && <span>{c.before_text}</span>}
                  <span className={`inline-flex min-w-[90px] justify-center px-3 py-1 rounded-[8px] border-2 border-dashed text-[14px] font-semibold ${selectedOpt ? 'border-[#3973bf] text-[#3973bf] bg-blue-50' : 'border-gray-300 text-gray-400'}`}>
                    {selectedOpt ? selectedOpt.text : '…'}
                  </span>
                  {c.after_text && <span>{c.after_text}</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(c.options ?? []).map((o) => {
                    const selected = ca?.selected_option_id === o.id;
                    return (
                      <button
                        key={o.id}
                        onClick={() => setCaseAnswer(q, c.id, { selected_option_id: o.id })}
                        className={`px-4 py-2 rounded-[10px] border text-[14px] font-medium transition-colors ${
                          selected ? 'border-[#3973bf] bg-blue-50 text-[#3973bf]' : 'border-gray-200 text-[#2b2b2b] hover:border-gray-300'
                        }`}
                      >
                        {o.text}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (q.question_type === 'true_false') {
      return (
        <div className="flex flex-col gap-2">
          {(q.cases ?? []).map((c, ci) => {
            const ca = v2[q.question_id!]?.cases?.[c.id];
            return (
              <div key={c.id} className="flex items-center gap-3 rounded-[14px] border border-gray-200 p-3">
                <span className="w-[26px] h-[26px] shrink-0 rounded-full bg-[#3973bf]/10 text-[#3973bf] flex items-center justify-center text-[13px] font-bold">
                  {ci + 1}
                </span>
                <span className="flex-1 text-[15px] text-[#2b2b2b]">{c.statement_text}</span>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => setCaseAnswer(q, c.id, { selected_bool: true })}
                    className={`px-4 py-2 rounded-[10px] border text-[13px] font-semibold ${ca?.selected_bool === true ? 'border-[#59e56e] bg-[#59e56e]/10 text-[#179e3b]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  >Doğru</button>
                  <button
                    onClick={() => setCaseAnswer(q, c.id, { selected_bool: false })}
                    className={`px-4 py-2 rounded-[10px] border text-[13px] font-semibold ${ca?.selected_bool === false ? 'border-[#e53e3e] bg-[#e53e3e]/10 text-[#e53e3e]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  >Yanlış</button>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // video_only — unscored solution video.
    return (
      <div>
        {q.video_url ? (
          <VideoWithSpeed src={q.video_url} className="w-full rounded-[12px] bg-black" />
        ) : (
          <div className="bg-[#f6f6f6] border border-gray-200 rounded-[12px] p-4 text-[13px] text-gray-500 text-center">
            Çözüm videosu henüz yüklenmemiş.
          </div>
        )}
        <p className="mt-3 text-[13px] text-gray-500 text-center">Bu soru puanlanmaz, çözüm videosunu izleyip devam edebilirsin.</p>
      </div>
    );
  };

  // ── Question body (cover) ──────────────────────────────────────────────────
  const renderCover = (q: SectionQuestionV2, keyPrefix: string) => (
    <React.Fragment key={`${keyPrefix}-cover`}>
      {q.pre_text && <p className="text-center text-[16px] font-semibold text-[#2b2b2b] mb-4">{q.pre_text}</p>}
      {q.cover_type === 'text' && q.cover_text && (
        <div className="bg-white rounded-[16px] border border-gray-200 p-5 mb-5 text-[15px] text-[#2b2b2b] whitespace-pre-wrap">{q.cover_text}</div>
      )}
      {q.cover_type === 'audio' && q.cover_media_url && (
        <div className="mb-5"><audio controls src={q.cover_media_url} className="w-full" /></div>
      )}
      {q.cover_type === 'video' && q.cover_media_url && (
        <div className="mb-5"><VideoWithSpeed src={q.cover_media_url} className="w-full rounded-[12px] bg-black" /></div>
      )}
      {(q.cover_type === 'image' || !isNewModel(q)) && (
        q.image_url ? (
          <div className="bg-white rounded-[16px] border border-gray-200 p-4 mb-5 flex justify-center">
            <img
              src={q.image_url}
              alt="Soru"
              draggable={false}
              className="max-w-full"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            />
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-[12px] p-4 mb-5 text-[13px] text-yellow-800">
            Bu soru için görsel henüz yüklenmedi.
          </div>
        )
      )}
    </React.Fragment>
  );

  // ── Review rendering (per-question, both models) ───────────────────────────
  const renderReview = (q: SectionQuestionV2, ra: ReviewAnswer | undefined) => {
    if (!isAnswerable(q)) {
      return <p className="text-[13px] text-gray-500">Bu soru puanlanmaz (çözüm videosu).</p>;
    }
    if (!ra) {
      return <p className="text-[13px] text-gray-500">Bu soru puanlanmadı (cevap anahtarı tanımlı değil).</p>;
    }
    // Durum types — per-case verdicts from detail_json.
    if (ra.detail?.cases?.length && isNewModel(q)) {
      const byCase = new Map(ra.detail.cases.map((c) => [c.case_id, c]));
      return (
        <div className="flex flex-col gap-2">
          {(q.cases ?? []).map((c, ci) => {
            const d = byCase.get(c.id);
            const optText = (id?: number | null) => (c.options ?? []).find((o) => o.id === id)?.text ?? '-';
            return (
              <div key={c.id} className={`rounded-[12px] border-2 p-3 ${d?.is_correct ? 'border-[#59e56e] bg-[#59e56e]/5' : 'border-[#e53e3e] bg-[#e53e3e]/5'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-[22px] h-[22px] rounded-full bg-white border flex items-center justify-center text-[12px] font-bold">{caseLabel(ci, q.listing_style)}</span>
                  {c.statement_text && <span className="text-[14px] text-[#2b2b2b]">{c.statement_text}</span>}
                  {c.before_text != null && (
                    <span className="text-[14px] text-[#2b2b2b]">{c.before_text} <b>___</b> {c.after_text}</span>
                  )}
                </div>
                <p className="text-[13px]">
                  {q.question_type === 'true_false' ? (
                    <>Cevabın: <b>{d?.selected_bool == null ? 'Boş' : d.selected_bool ? 'Doğru' : 'Yanlış'}</b> · Doğrusu: <b>{d?.correct_bool ? 'Doğru' : 'Yanlış'}</b></>
                  ) : (
                    <>Cevabın: <b>{d?.selected_option_id == null ? 'Boş' : optText(d.selected_option_id)}</b> · Doğrusu: <b className="text-[#179e3b]">{optText(d?.correct_option_id)}</b></>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      );
    }
    // MC (both models) — letter pill review, same visual as the legacy runner.
    const maxOpts = isNewModel(q) ? (q.options?.length ?? 5) : section!.max_options;
    return (
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          {OPTION_LETTERS.slice(0, maxOpts).split('').map((letter, i) => {
            const isCorrect = ra.correct_option === letter;
            const isSelected = ra.selected_option === letter;
            let cls = 'border-gray-200';
            if (isCorrect) cls = 'border-[#59e56e] bg-[#59e56e]/10';
            else if (isSelected && !isCorrect) cls = 'border-[#e53e3e] bg-[#e53e3e]/10';
            return (
              <div key={letter} className={`flex-1 flex flex-col items-center justify-center gap-1 p-3 rounded-[12px] border-2 ${cls}`}>
                <span className="text-[15px] font-semibold text-[#2b2b2b]">{letter}</span>
                {isNewModel(q) && !isLetterOnlyMc(q) && q.options?.[i] && (
                  <span className="text-[12px] text-gray-600 text-center">{q.options[i].text}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Screens ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !section) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center p-8 gap-4">
        <p className="text-gray-600 text-center">{error ?? 'Bölüm bulunamadı.'}</p>
        <button onClick={onClose} className="bg-[#3973bf] text-white px-6 py-2.5 rounded-[12px]">Kapat</button>
      </div>
    );
  }

  if (result && phase === 'summary') {
    const blank = result.review.filter((r) => r.selected_option === null && !(r.detail?.cases?.some((c) => c.selected_option_id != null || c.selected_bool != null))).length;
    const wrong = result.review.length - result.score_correct - blank;
    const pct = result.points_total
      ? Math.round(((result.points_correct ?? 0) / result.points_total) * 100)
      : result.score_total > 0 ? Math.round((result.score_correct / result.score_total) * 100) : 0;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
        <div className="bg-white rounded-[20px] w-full max-w-[560px] p-8 text-center">
          <h2 className="text-[26px] font-bold text-[#3973bf] mb-4">Test Tamamlandı</h2>
          <p className="text-[14px] text-gray-500 mb-6">{displayBookSectionLabel(section)}</p>
          <div className="grid grid-cols-4 gap-2 mb-6">
            <div className="rounded-[14px] p-3 border border-gray-200"><p className="text-[11px] text-gray-500">Toplam</p><p className="text-[20px] font-bold">{result.score_total}</p></div>
            <div className="rounded-[14px] p-3 border-2 border-[#59e56e]"><p className="text-[11px] text-[#59e56e]">Doğru</p><p className="text-[20px] font-bold text-[#59e56e]">{result.score_correct}</p></div>
            <div className="rounded-[14px] p-3 border-2 border-[#e53e3e]"><p className="text-[11px] text-[#e53e3e]">Yanlış</p><p className="text-[20px] font-bold text-[#e53e3e]">{Math.max(0, wrong)}</p></div>
            <div className="rounded-[14px] p-3 border-2 border-[#f5c518]"><p className="text-[11px] text-[#a97e00]">Boş</p><p className="text-[20px] font-bold text-[#a97e00]">{blank}</p></div>
          </div>
          <div className="rounded-[14px] bg-[#3973bf] text-white p-4 mb-6">
            <p className="text-[13px] opacity-90">Başarı</p>
            <p className="text-[28px] font-bold">%{pct}</p>
            {result.points_total != null && result.points_total > 0 && (
              <p className="text-[13px] opacity-90 mt-1">Puan: {result.points_correct ?? 0} / {result.points_total}</p>
            )}
          </div>
          <p className="text-[13px] text-gray-500 mb-6">Süre: {formatMMSS(result.time)}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setPhase('review')}
              className="bg-[#e5599b] text-white text-[15px] font-semibold px-6 py-3 rounded-[12px] hover:bg-pink-600"
            >Cevapları Gör</button>
            <button onClick={onClose} className="bg-[#3973bf] text-white text-[15px] font-semibold px-8 py-3 rounded-[12px] hover:bg-blue-700">Kapat</button>
          </div>
        </div>
      </div>
    );
  }

  if (result && phase === 'review') {
    const q = questions[reviewIdx];
    const ra = q ? result.review.find((r) => (q.question_id != null && r.question_id === q.question_id) || r.question_number === q.question_number) : undefined;
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <p className="text-[16px] font-bold text-[#2b2b2b]">{displayBookSectionLabel(section)} - Cevaplar</p>
            <p className="text-[13px] text-gray-500">{result.score_correct}/{result.score_total} doğru</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setPhase('summary')} className="bg-gray-200 text-[#2b2b2b] text-[14px] font-semibold px-5 py-2.5 rounded-[12px] hover:bg-gray-300">Özete Dön</button>
            <button onClick={onClose} className="bg-[#3973bf] text-white text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-blue-700">Bitir</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[900px] mx-auto">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[14px] font-bold text-[#2b2b2b]">Soru {q?.question_number}</p>
              {ra && (
                <span className={`text-[12px] font-semibold px-3 py-1 rounded-full ${ra.is_correct ? 'bg-[#59e56e]/20 text-[#179e3b]' : ra.selected_option == null && !ra.detail ? 'bg-[#f5c518]/20 text-[#a97e00]' : 'bg-[#e53e3e]/20 text-[#e53e3e]'}`}>
                  {ra.is_correct ? 'Doğru' : ra.points_earned ? `Kısmen (${ra.points_earned}/${ra.points_total})` : ra.selected_option == null && !ra.detail ? 'Boş' : 'Yanlış'}
                </span>
              )}
            </div>
            {q && renderCover(q, 'review')}
            {q && renderReview(q, ra)}
            <div className="mt-5">
              <p className="text-[13px] font-semibold text-[#2b2b2b] mb-2">Soru Çözüm Videosu</p>
              {q?.video_url ? (
                <VideoWithSpeed src={q.video_url} className="w-full rounded-[12px] bg-black" />
              ) : (
                <div className="bg-[#f6f6f6] border border-gray-200 rounded-[12px] p-4 text-[13px] text-gray-500 text-center">
                  Bu soru için çözüm videosu henüz yok.
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t">
          <div className="flex gap-2 flex-wrap">
            {questions.map((qq, i) => {
              const r = result.review.find((x) => (qq.question_id != null && x.question_id === qq.question_id) || x.question_number === qq.question_number);
              const bg = !isAnswerable(qq) ? 'bg-gray-300 text-white' :
                !r ? 'bg-gray-200 text-[#2b2b2b]' :
                reviewIdx === i ? 'bg-[#3973bf] text-white ring-2 ring-[#3973bf]' :
                r.is_correct ? 'bg-[#59e56e] text-white' :
                r.selected_option == null && !r.detail ? 'bg-[#f5c518] text-white' :
                'bg-[#e53e3e] text-white';
              return (
                <button key={i} onClick={() => setReviewIdx(i)} className={`w-[32px] h-[32px] rounded-[8px] text-[12px] font-bold ${bg}`}>{qq.question_number}</button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Optik (answer-sheet) phase ─────────────────────────────────────────────
  if (optik) {
    const totalPointsO = section.points_total;
    return (
      <div className="fixed inset-0 z-50 bg-[#f6f7fb] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
          <div className="flex items-center gap-3">
            <div className="w-[38px] h-[38px] rounded-[10px] bg-[#e5599b]/10 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e5599b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="3" width="16" height="18" rx="2" /><circle cx="9" cy="9" r="1.5" fill="#e5599b" stroke="none" /><line x1="13" y1="9" x2="17" y2="9" /><circle cx="9" cy="14" r="1.5" /><line x1="13" y1="14" x2="17" y2="14" />
              </svg>
            </div>
            <div>
              <p className="text-[16px] font-bold text-[#2b2b2b]">{displayBookSectionLabel(section)} - Optik</p>
              <p className="text-[13px] text-gray-500">
                {section.question_count} Soru{totalPointsO != null && totalPointsO > 0 ? ` · ${totalPointsO} Puan` : ''} · Kitaptan çöz, buraya işaretle
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-gray-100 rounded-[10px] px-3 py-2">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="text-[16px] font-bold text-[#3973bf] tabular-nums">{formatMMSS(elapsed)}</span>
            </div>
            <button
              onClick={onClose}
              className="bg-gray-200 text-[#2b2b2b] text-[14px] font-semibold px-5 py-2.5 rounded-[12px] hover:bg-gray-300"
              title="İşaretlerin kaydedildi; sonra devam edebilirsin."
            >
              Kaydet ve Çık
            </button>
            <button
              onClick={() => setConfirmSubmit(true)}
              disabled={submitting}
              className="bg-[#3973bf] text-white text-[14px] font-semibold px-6 py-2.5 rounded-[12px] disabled:opacity-50"
            >
              {submitting ? 'Gönderiliyor…' : 'Bitir ve Gönder'}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1100px] mx-auto">
            <div className="mb-5 rounded-[14px] border border-[#3973bf]/20 bg-[#3973bf]/5 px-4 py-3 flex items-start gap-3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <p className="text-[13px] text-[#2b2b2b] leading-relaxed">
                Soruları <strong>kitabından</strong> çöz, doğru şıkkı aşağıdaki optikte işaretle. Süren ve skorun performans tablonuza kaydedilir. Bir şıkka tekrar tıklayarak işaretini kaldırabilirsin.
              </p>
            </div>

            <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
              {questions.map((qq) => {
                const answerableRow = isOptikAnswerable(qq);
                const selected = answers[qq.question_number];
                return (
                  <div
                    key={qq.question_number}
                    className={`flex items-center gap-3 rounded-[12px] border px-3 py-2.5 bg-white ${
                      selected ? 'border-[#3973bf]/40 shadow-sm' : 'border-gray-200'
                    }`}
                  >
                    <span className={`w-[34px] h-[34px] shrink-0 rounded-[9px] flex items-center justify-center text-[14px] font-bold tabular-nums ${
                      selected ? 'bg-[#3973bf] text-white' : 'bg-gray-100 text-[#2b2b2b]'
                    }`}>
                      {qq.question_number}
                    </span>
                    {answerableRow ? (
                      <div className="flex gap-1.5 flex-1 justify-end">
                        {OPTION_LETTERS.slice(0, optikMaxOpts(qq)).split('').map((letter) => {
                          const on = selected === letter;
                          return (
                            <button
                              key={letter}
                              onClick={() => setOptikAnswer(qq, letter)}
                              aria-label={`Soru ${qq.question_number} şık ${letter}`}
                              className={`w-[36px] h-[36px] rounded-full border-2 flex items-center justify-center text-[14px] font-bold transition-colors ${
                                on
                                  ? 'border-[#3973bf] bg-[#3973bf] text-white'
                                  : 'border-gray-300 text-gray-500 hover:border-[#3973bf] hover:text-[#3973bf]'
                              }`}
                            >
                              {letter}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="flex-1 text-right text-[12px] text-gray-400 italic">
                        Test ekranından çöz
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {submitError && <p className="text-[13px] text-red-500 mt-4">{submitError}</p>}
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t bg-white">
          <p className="text-[13px] text-gray-500">
            İşaretlenen: <span className="font-semibold text-[#2b2b2b]">{optikAnsweredCount}</span> / {optikAnswerable.length}
          </p>
          <button
            onClick={() => setConfirmSubmit(true)}
            disabled={submitting}
            className="bg-[#3973bf] text-white text-[14px] font-semibold px-6 py-2.5 rounded-[12px] disabled:opacity-50 hover:bg-blue-700"
          >
            {submitting ? 'Gönderiliyor…' : 'Bitir ve Gönder'}
          </button>
        </div>

        {confirmSubmit && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-[20px] w-full max-w-[420px] p-6 text-center">
              <p className="text-[17px] font-semibold text-[#2b2b2b] mb-2">Optiği göndermek ister misin?</p>
              <p className="text-[13px] text-gray-500 mb-5">
                İşaretlenen: {optikAnsweredCount} / {optikAnswerable.length}
                {optikAnsweredCount < optikAnswerable.length && (
                  <span className="block mt-1 text-[#e5599b]">{optikAnswerable.length - optikAnsweredCount} soru boş kalacak.</span>
                )}
              </p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => setConfirmSubmit(false)} className="bg-gray-100 text-[#2b2b2b] text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-gray-200">Vazgeç</button>
                <button onClick={submit} disabled={submitting} className="bg-[#3973bf] text-white text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Gönderiliyor…' : 'Evet, Gönder'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Taking phase ───────────────────────────────────────────────────────────
  const q = questions[currentIdx];
  const prevIdx = currentIdx > 0 ? currentIdx - 1 : null;
  const nextIdx = currentIdx < questions.length - 1 ? currentIdx + 1 : null;
  const totalPoints = section.points_total;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div>
          <p className="text-[16px] font-bold text-[#2b2b2b]">{displayBookSectionLabel(section)}</p>
          <p className="text-[13px] text-gray-500">
            {section.question_count} Soru{totalPoints != null && totalPoints > 0 ? ` · ${totalPoints} Puan` : ''}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-gray-100 rounded-[10px] px-3 py-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="text-[16px] font-bold text-[#3973bf] tabular-nums">{formatMMSS(elapsed)}</span>
          </div>
          <button
            onClick={onClose}
            className="bg-gray-200 text-[#2b2b2b] text-[14px] font-semibold px-5 py-2.5 rounded-[12px] hover:bg-gray-300"
            title="Yanıtlar kaydedildi; sonra devam edebilirsin."
          >
            Kaydet ve Çık
          </button>
          <button
            onClick={() => setConfirmSubmit(true)}
            disabled={submitting}
            className="bg-[#3973bf] text-white text-[14px] font-semibold px-6 py-2.5 rounded-[12px] disabled:opacity-50"
          >
            {submitting ? 'Gönderiliyor…' : 'Bitir ve Gönder'}
          </button>
        </div>
      </div>

      <div
        className="flex-1 overflow-hidden flex"
        onContextMenu={(e) => e.preventDefault()}
        style={{ userSelect: 'none' }}
      >
        <div className="flex-1 relative">
          <div className="absolute inset-0 overflow-y-auto p-6">
            <div className="max-w-[760px] mx-auto pb-16">
              <div className="flex items-center mb-4">
                <div className="flex-1 h-[44px] rounded-[12px] border border-gray-200 flex items-center justify-center gap-3">
                  <span className="text-[15px] font-bold text-[#2b2b2b]">Soru {q?.question_number}</span>
                  {isNewModel(q ?? {} as SectionQuestionV2) && q?.points != null && (
                    <span className="text-[12px] font-semibold text-[#3973bf] bg-[#3973bf]/10 px-2 py-0.5 rounded-full">{q.points} Puan</span>
                  )}
                </div>
              </div>

              {q && renderCover(q, 'take')}
              {q && renderAnswerArea(q)}

              {q && isAnswered(q) && (nextIdx != null ? (
                <button
                  onClick={() => setCurrentIdx(nextIdx)}
                  className="mt-5 w-full flex items-center justify-center gap-2 bg-[#e5599b] text-white text-[15px] font-semibold py-3 rounded-[12px] hover:bg-pink-600 transition-colors"
                >
                  Sonraki Soru
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              ) : (
                // Last question — finishing it goes straight to the submit
                // confirmation, same as the header's "Bitir ve Gönder".
                <button
                  onClick={() => setConfirmSubmit(true)}
                  disabled={submitting}
                  className="mt-5 w-full flex items-center justify-center gap-2 bg-[#3973bf] text-white text-[15px] font-semibold py-3 rounded-[12px] hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Gönderiliyor…' : 'Testi Bitir'}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </button>
              ))}
              {submitError && <p className="text-[13px] text-red-500 mt-4">{submitError}</p>}
            </div>
          </div>

          <button
            onClick={() => prevIdx != null && setCurrentIdx(prevIdx)}
            disabled={prevIdx == null}
            aria-label="Önceki soru"
            className={`absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-10 w-[60px] h-[120px] rounded-[16px] flex items-center justify-center shadow-lg transition-colors ${
              prevIdx == null ? 'bg-white text-gray-300 border border-gray-200 cursor-not-allowed shadow-sm' : 'bg-[#3973bf] text-white hover:bg-blue-700'
            }`}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            onClick={() => nextIdx != null && setCurrentIdx(nextIdx)}
            disabled={nextIdx == null}
            aria-label="Sonraki soru"
            className={`absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-10 w-[60px] h-[120px] rounded-[16px] flex items-center justify-center shadow-lg transition-colors ${
              nextIdx == null ? 'bg-white text-gray-300 border border-gray-200 cursor-not-allowed shadow-sm' : 'bg-[#3973bf] text-white hover:bg-blue-700'
            }`}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>

        <div className="w-[320px] shrink-0 border-l border-gray-200 bg-gray-50/60 overflow-y-auto p-4 flex flex-col">
          <p className="text-[13px] font-semibold text-[#2b2b2b] mb-3 px-1">Sorular</p>
          <div className="flex flex-col gap-1.5">
            {questions.map((qq, i) => {
              const isCurrent = currentIdx === i;
              const answered = isAnswered(qq);
              const canAnswer = letterAnswerable(qq);
              const sel = canAnswer ? selectedLetterFor(qq) : null;
              return (
                <div
                  key={i}
                  className={`flex items-center gap-1.5 rounded-[10px] p-1 transition-colors ${
                    isCurrent ? 'bg-[#3973bf]/10 ring-1 ring-[#3973bf]/30' : 'hover:bg-gray-100'
                  }`}
                >
                  {/* Number — click to jump to the question */}
                  <button
                    onClick={() => setCurrentIdx(i)}
                    title={`Soru ${qq.question_number}`}
                    className={`w-[30px] h-[30px] shrink-0 rounded-[8px] text-[12px] font-bold flex items-center justify-center ${
                      isCurrent ? 'bg-[#3973bf] text-white'
                      : !isAnswerable(qq) ? 'bg-gray-100 border border-gray-200 text-gray-400'
                      : answered ? 'bg-[#e5599b] text-white'
                      : 'bg-white border border-gray-200 text-[#2b2b2b]'
                    }`}
                  >{qq.question_number}</button>
                  {/* A–E quick answer — writes the same state as the main area */}
                  {canAnswer ? (
                    <div className="flex gap-1 flex-1 justify-end">
                      {OPTION_LETTERS.slice(0, letterCountFor(qq)).split('').map((letter, li) => {
                        const on = sel === letter;
                        return (
                          <button
                            key={letter}
                            onClick={() => pickLetter(qq, li)}
                            aria-label={`Soru ${qq.question_number} şık ${letter}`}
                            className={`w-[30px] h-[30px] rounded-full border text-[12px] font-bold transition-colors ${
                              on
                                ? 'border-[#3973bf] bg-[#3973bf] text-white'
                                : 'border-gray-300 text-gray-500 bg-white hover:border-[#3973bf] hover:text-[#3973bf]'
                            }`}
                          >{letter}</button>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="flex-1 text-right text-[11px] text-gray-400 italic pr-1">
                      {isAnswerable(qq) ? 'Soruda çöz' : 'Video'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-auto pt-4 text-[13px] text-gray-500 px-1">
            İşaretlenen: {answeredCount} / {answerable.length}
          </div>
        </div>
      </div>

      {confirmSubmit && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-[420px] p-6 text-center">
            <p className="text-[17px] font-semibold text-[#2b2b2b] mb-2">Testi bitirmek ve göndermek ister misin?</p>
            <p className="text-[13px] text-gray-500 mb-5">
              İşaretlenen: {answeredCount} / {answerable.length}
              {answeredCount < answerable.length && (
                <span className="block mt-1 text-[#e5599b]">{answerable.length - answeredCount} soru eksik/boş kalacak.</span>
              )}
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setConfirmSubmit(false)} className="bg-gray-100 text-[#2b2b2b] text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-gray-200">Vazgeç</button>
              <button onClick={submit} disabled={submitting} className="bg-[#3973bf] text-white text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Gönderiliyor…' : 'Evet, Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestRunner;
